import 'dayjs/plugin/timezone';
import 'dayjs/plugin/utc';
import dayjs from 'dayjs';
import humps from 'humps';
import { LinkifyIt } from 'linkify-it';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import map from 'lodash/map';
import zipObject from 'lodash/zipObject';

import { createHttpClient } from '@/apis/http';
import { FILTERS_OPERATORS } from '@/config';
import { enumTypes } from '@/models/pinterest.enum';
import { resolveTimezone } from '@/utils/date';
import { differenceBetweenDays, getCurrentDate } from '@/utils/dateUtils';
import { timeseriesHourlyTooltip, timeseriesYearSuffix } from '@/utils/formatters';
import { LINKIFY_OPTIONS } from '@/utils/linkify-options';
import { appendQueryParamsToUrl } from '@/utils/query';
import { DATE_ONLY, guessTimezone } from '@/utils/timezone';

const externalHttp = createHttpClient();

interface DateOption {
  value: number;
  label: string;
}

interface YourEngagementMetric {
  date: string;
  data_status: string;
  metrics: Record<string, number | string>;
}

export interface YourEngagementPayload {
  data?: {
    daily_metrics?: YourEngagementMetric[];
    monthly_metrics?: YourEngagementMetric[];
    summary_metrics?: Record<string, number>;
  } | null;
}

interface FollowerMetrics {
  followers_ALL: number;
  followers_ALL_AGGR: number;
}

interface FollowerChartDataV5 {
  NET_NEW_FOLLOWERS: Record<string, number>;
  TOTAL_FOLLOWERS: Record<string, number>;
}

interface RawMediaItem {
  id?: string | number;
  mediaId?: string | number;
  urls?: { ratio?: string | null };
  width?: number;
  height?: number;
  originalWidth?: number;
  originalHeight?: number;
  brandType?: string;
  sourceId?: string | number;
  createdAt?: string;
  predictions?: unknown;
  insights?: unknown;
  [key: string]: unknown;
}

interface GalleryMedia {
  id: string | number;
  urls?: { ratio?: string | null };
  originalWidth?: number;
  originalHeight?: number;
  source?: string;
  sourceData?: unknown;
  sourceId?: string | number;
  type?: string;
  createdAt?: string;
  predictions?: unknown;
}

interface GalleryItem {
  media: GalleryMedia;
  brand_media_type: string;
  products?: unknown;
  clicks?: unknown;
  canPublishWithin?: unknown;
}

interface NestedOption {
  value?: unknown;
  children?: NestedOption[] | null;
  [key: string]: unknown;
}

export function generateMixpanelDateLabels(
  dateRange: [string, string] | null | undefined,
  dateOptions: DateOption[],
): string {
  if (!dateRange) {
    return 'All Time';
  }
  if (dateRange[1] !== getCurrentDate()) {
    return 'Custom';
  }
  const selectedOption = dateOptions.find(
    (option) => option.value === differenceBetweenDays(dateRange[0], dateRange[1]),
  );
  return selectedOption?.label || 'Custom';
}

export function formatYourEngagementChartData(
  payload: YourEngagementPayload | null | undefined,
  scale: string,
  metric: string,
): {
  labels: string[] | undefined;
  dateValues: string[] | undefined;
  values: (number | string)[] | undefined;
  total: number | undefined;
  average: string | null;
} | null {
  const chartData = payload?.data;

  if (!chartData) {
    return null;
  }

  let statData;

  if (scale === enumTypes.DAILY) {
    statData = chartData.daily_metrics;
  } else {
    statData = chartData.monthly_metrics;
  }

  const dateValues = statData?.map((d) => d.date);
  const labels = dateValues?.map((date) => {
    let title = dayjs(date).format('MMM DD');
    title = timeseriesYearSuffix(date, title);
    return title;
  });
  const values = statData?.map((item) => {
    let stat = item.metrics[metric.toLowerCase()];
    if (stat && item.data_status === 'READY' && metric === enumTypes.ENGAGEMENT_RATE) {
      stat = (parseFloat(String(stat)) * 100).toFixed(2);
    }
    return stat;
  });
  const total = chartData?.summary_metrics?.[metric.toLowerCase()];

  // calculate average engagement rate
  let average = null;
  if (metric === enumTypes.ENGAGEMENT_RATE) {
    // avoid dividing by 0
    if (chartData?.summary_metrics && chartData.summary_metrics.impressions > 0) {
      average = (
        (parseFloat(String(chartData.summary_metrics.engagements)) /
          parseFloat(String(chartData.summary_metrics.impressions))) *
        100
      ).toFixed(2);
    }
  }
  return { labels, dateValues, values, total, average };
}

export function formatFollowerChartData(
  data: Record<string, FollowerMetrics>,
  scale = 'DAILY',
): {
  labels: string[];
  values: number[];
  valuesTotal: number[];
  totalNew: number;
  total: number;
  average: number;
  endOfRangeTotalFollowers: number;
} {
  let labels;
  if (scale === enumTypes.HOURLY) {
    labels = Object.keys(data).map((date) => timeseriesHourlyTooltip(date));
  } else {
    labels = Object.keys(data).map((date) => {
      let title = dayjs(date).format('MMM DD');
      title = timeseriesYearSuffix(date, title);
      return title;
    });
  }
  const values = Object.values(data).map((newFollowers) => newFollowers.followers_ALL);
  const valuesTotal = Object.values(data).map(
    (totalFollowers) => totalFollowers.followers_ALL_AGGR,
  );
  const endOfRangeTotalFollowers = valuesTotal[valuesTotal.length - 1];
  const totalNew = values.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
  const total = valuesTotal.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
  const average = Math.floor(totalNew / values.length);
  return { labels, values, valuesTotal, totalNew, total, average, endOfRangeTotalFollowers };
}

export function formatFollowerChartDataV5(
  data: FollowerChartDataV5,
  scale?: string,
): {
  labels: string[];
  values: number[];
  valuesTotal: number[];
  totalNew: number;
  total: number;
  average: number;
  endOfRangeTotalFollowers: number;
} {
  const labels = Object.keys(data.NET_NEW_FOLLOWERS).map((date) => {
    const format = scale === enumTypes.MONTHLY ? 'MMM' : 'MMM DD';
    return timeseriesYearSuffix(date, dayjs(date).format(format));
  });
  const values = Object.values(data.NET_NEW_FOLLOWERS);
  const valuesTotal = Object.values(data.TOTAL_FOLLOWERS);
  const endOfRangeTotalFollowers = valuesTotal[valuesTotal.length - 1];
  const totalNew = values.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
  const total = valuesTotal.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
  const average = Math.floor(totalNew / values.length);
  return { labels, values, valuesTotal, totalNew, total, average, endOfRangeTotalFollowers };
}

export function formatMediaListData(data: unknown) {
  // Format because data from library is already camelized, but data from other backends may not be
  const camelizedData = humps.camelizeKeys(data) as RawMediaItem[] | null | undefined;
  return (
    camelizedData?.map((media) => ({
      // Pinterest returns id and others returns mediaId, maybe unify them in the backend
      // at some point.
      ...media,
      id: media.id ? media.id : media.mediaId,
      url: media.urls ? media.urls.ratio : null,
      width: media.width ? media.width : media.originalWidth,
      height: media.height ? media.height : media.originalHeight,
      sourceType: media.brandType,
      sourceId: media.sourceId,
      imageUrl: media.urls ? media.urls.ratio : null,
      postDate: dayjs(media.createdAt).format('MMMM D h:m a'),
      predictions: media.predictions,
      insights: media?.insights,
    })) ?? []
  );
}

export function formatGalleryMediaListData(data: GalleryItem[] | { data: GalleryItem[] }) {
  // sometimes it is an array, some time it is a dict with { data: [], next: Obj }
  const dataList = Array.isArray(data) ? data : data.data;
  return dataList.map((item) => {
    const { media, brand_media_type: brandMediaType } = item;
    return {
      id: media.id,
      url: media.urls ? media.urls.ratio : null,
      urls: media.urls,
      width: media.originalWidth,
      height: media.originalHeight,
      source: media.source,
      sourceType: brandMediaType,
      sourceData: media.sourceData,
      sourceId: media.sourceId,
      mediaType: media.type,
      imageUrl: media.urls ? media.urls.ratio : null,
      postDate: dayjs(media.createdAt).format('MMMM D h:m a'),
      predictions: media.predictions,
      products: item.products,
      clicks: item.clicks,
      canPublishWithin: item.canPublishWithin,
    };
  });
}

export function dragSelectableItemGenerator(
  _contextEl: Element | null | undefined,
  activeMedia: { id: string | number }[],
  targetItemClassList: string | null | undefined,
  selector: string,
): () => { el: HTMLElement; item: { id: string | number } | undefined }[] {
  return () => {
    const elements = targetItemClassList
      ? (document.getElementsByClassName(targetItemClassList) as HTMLCollectionOf<HTMLElement>)
      : document.querySelectorAll<HTMLElement>(selector);
    return map(elements, (el) => {
      const dataId = el.dataset.id;
      const item = find(activeMedia, (x) => dataId === String(x.id));
      return { el, item };
    });
  };
}

export function truncateUrl(url: string, maxLen: number): string {
  let formattedUrl = url;
  if (formattedUrl.substring(0, 8) === 'https://') {
    formattedUrl = formattedUrl.substring(8);
  } else if (formattedUrl.substring(0, 7) === 'http://') {
    formattedUrl = formattedUrl.substring(7);
  }
  if (formattedUrl.substring(0, 4) === 'www.') {
    formattedUrl = formattedUrl.substring(4);
  }
  if (formattedUrl.length > maxLen) {
    return `${formattedUrl.substring(0, maxLen - 3)}...`;
  }
  return formattedUrl;
}

export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      if (!canvas.toBlob) {
        throw new Error('canvas.toBlob is not supported in this browser');
      }

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob from canvas'));
            return;
          }
          resolve(blob);
        },
        'image/jpeg',
        1,
      );
    } catch (error) {
      reject(error);
    }
  });
}

export function recursiveObjectPromiseAll(
  obj: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const keys = Object.keys(obj);
  return Promise.all(
    keys.map((key) => {
      const value = obj[key];
      if (typeof value === 'object' && !(value as PromiseLike<unknown>).then) {
        return recursiveObjectPromiseAll(value as Record<string, unknown>);
      }
      return value;
    }),
  ).then((result) => zipObject(keys, result));
}

/**
 * How long to hold a download's blob URL before revoking it. Deliberately generous: the assumption
 * is only that the browser has *started* fetching the blob by then, which is not something we can
 * observe. FileSaver.js waits 40s for the same hazard, and the asymmetry favours waiting — too short
 * silently truncates an export on whichever browser is slow, and neither the unit suite nor the
 * e2e suite (both Chrome) would catch it, while too long just pins one blob a while longer.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = 40_000;

/**
 * Revoke a blob object URL after an `<a download>` click has had time to start fetching it.
 *
 * Firefox/Safari may not have started that fetch when `click()` returns, and revoking too early
 * cancels the download. Safe to call with a non-blob URL (e.g. a plain https link):
 * `revokeObjectURL` is a no-op for those.
 *
 * Not vetted for `window.open(blobUrl)`, which navigates a new document rather than starting a
 * fetch, so a throttled or backgrounded tab could still lose it.
 * `CreatorEmailAttachment.openAttachment` shares this timer by convenience, not because the delay is
 * known to be sufficient for that case.
 */
export function revokeObjectUrlAfterDownload(url: string): void {
  setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_REVOKE_DELAY_MS);
}

export function downloadFileFromMemory(data: BlobPart, filename: string): void {
  const url = URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  revokeObjectUrlAfterDownload(url);
}

export function downloadFileFromUrl(url: string, filename = ''): void {
  const link = document.createElement('a');
  link.href = appendQueryParamsToUrl(
    url,
    filename ? { download: true, filename } : { download: true },
  );
  if (filename) link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Attempt to determine file type from hosted file if ext not provided in url
export async function determineFileTypeFromUrl(url: string): Promise<string | false | undefined> {
  const fileSignatureMap: Record<string, string> = {
    '89 50 4e 47': 'png',
    'ff d8 ff': 'jpeg',
    '00 00 00 18 66 74 79 70': 'mp4',
    '00 00 00 20 66 74 79 70 71 74': 'mov',
    '52 49 46 46': 'avi',
  };
  const fileRes = await externalHttp.get<ArrayBuffer>(url, { responseType: 'arraybuffer' });
  if (fileRes.status === 200 && fileRes.data) {
    const buffer = new Uint8Array(fileRes.data);
    const hexSignature = Array.from(buffer)
      .slice(0, 12) // Get first 12 bytes
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join(' ');
    const sig = find(Object.keys(fileSignatureMap), (signature) =>
      hexSignature.startsWith(signature),
    );
    return sig === undefined ? undefined : fileSignatureMap[sig];
  }
  return false;
}

export function openIGProfilePage(handle: string): void {
  window.open(`https://www.instagram.com/${handle}`, '_blank');
}

export const isRangeOperator = (operator: string): boolean =>
  [FILTERS_OPERATORS.BETWEEN, FILTERS_OPERATORS.NOT_BETWEEN].includes(operator);

export function convertUtcDateTimeToLocalDateTimeByZone(
  dateTime: dayjs.ConfigType,
  timezone?: string | null,
): dayjs.Dayjs {
  const tz = timezone || guessTimezone();
  return dayjs.utc(dateTime).tz(tz);
}

export function convertDateToUtcDateTimeByZone(
  date: dayjs.ConfigType,
  timezone?: string | null,
): dayjs.Dayjs {
  const userTimezone = timezone || guessTimezone();
  const localTimestamp = dayjs(date).tz(userTimezone, true);
  return localTimestamp.utc();
}

export function convertDateToUtcDateTimeByZoneString(
  date: dayjs.ConfigType,
  timezone?: string | null,
): string {
  return convertDateToUtcDateTimeByZone(date, timezone).format('YYYY-MM-DDTHH:mm:ss[Z]');
}

/**
 * A picked calendar date as the UTC instant its day begins at, anchored through
 * `resolveTimezone` so the day the app sends is the day the date components showed.
 * Pass the day itself for an end bound too: the backends expand it to the end of that day,
 * so sending its last second overshoots by a day.
 */
export function toUtcQueryDateTime(date: dayjs.ConfigType): string | undefined {
  if (date == null) return undefined;
  const timezone = resolveTimezone();
  // A `Date` names an instant, not a day, so read the day it falls on in that timezone —
  // rendering it through the browser's clock first would move the day for anyone whose zones differ.
  const day = DATE_ONLY.test(String(date))
    ? String(date)
    : dayjs(date).tz(timezone).format('YYYY-MM-DD');
  return convertDateToUtcDateTimeByZoneString(day, timezone);
}

export function convertDateToUtcDateTimeByZoneISOString(
  date: dayjs.ConfigType,
  timezone?: string | null,
): string {
  return convertDateToUtcDateTimeByZone(date, timezone).toISOString();
}

export function dropNullObjectFields<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const localObj: Partial<T> = cloneDeep(obj);
  Object.keys(localObj).forEach((key) => localObj[key] == null && delete localObj[key]);
  return localObj;
}

export function extractUrlsFromText(text: string): string[] {
  const linkify = new LinkifyIt(LINKIFY_OPTIONS);
  return (linkify.match(text) ?? [])
    .filter((match) => match.schema !== 'mailto:')
    .map((match) => match.raw)
    .filter((url) => !url.startsWith('file:///'))
    .filter((url) => !url.startsWith('ftp://'));
}

export const mapById = <T extends { id: PropertyKey }>(objects: T[]): Record<string, T> =>
  Object.fromEntries(objects.map((obj) => [obj.id, obj]));

// Finds the nested option that has the given value, if it exists
export function findNestedOption<T extends NestedOption>(
  value: unknown,
  options: T[] | null | undefined,
): T | undefined {
  return options?.reduce?.((result: T | undefined, option) => {
    if (result) return result;
    if (option.value === value) return option;
    if (option.children != null) return findNestedOption(value, option.children) as T | undefined;
    return undefined;
  }, undefined);
}

// Finds the index of the first option that has the given value or has a decendent with that value
export function findIndexNestedOption(
  value: unknown,
  options: NestedOption[] | null | undefined,
): number | undefined {
  return options?.findIndex?.((option) => {
    if (option.children) {
      const childIndex = findIndexNestedOption(value, option.children);
      return childIndex !== undefined && childIndex >= 0;
    }
    return option.value === value;
  });
}
